/**
 * The off-universe card — shown when a query resolves to no cached asset, offering
 * live scoring via the backend.
 */
import type { Asset } from '../../core/types.ts';
import { esc } from '../../core/format.ts';
import { backendAvailable } from '../backend.ts';

/** Invoked when the user asks for live scoring; resolves to the scored asset or null. */
export type ScoreHandler = (query: string) => Promise<Asset | null>;

export function buildOffUniverseCard(
  query: string,
  onScore?: ScoreHandler,
  onScored?: (asset: Asset) => void,
): HTMLElement {
  const div = document.createElement('div');
  div.className = 'notfound';
  const available = backendAvailable();
  const buttonLabel = available ? 'Score via Live Atlas Backend' : 'Live Backend Unavailable';
  const disabled = available ? '' : 'disabled';

  div.innerHTML =
    '<div class="header">Off-Universe Asset</div>' +
    `<div class="query">${esc(query)}</div>` +
    '<p class="body">The query does not resolve to a currently cached Atlas asset. Live dynamic scoring is available via the Atlas backend, which conducts web research across issuer disclosures, exchange filings, and authoritative data providers, then returns a structured scorecard generated under the Atlas methodology.</p>' +
    `<div class="action"><button type="button" data-offu-btn ${disabled}>${buttonLabel}</button></div>`;

  const btn = div.querySelector<HTMLButtonElement>('[data-offu-btn]');
  if (btn && available && onScore) {
    btn.addEventListener('click', () => {
      btn.disabled = true;
      btn.textContent = 'Researching...';
      void onScore(query).then((result) => {
        if (result?.code) {
          onScored?.(result);
        } else {
          btn.disabled = false;
          btn.textContent = 'Scoring Failed — Retry';
        }
      });
    });
  }
  return div;
}
